import { MountingPortal } from 'portal-vue';
import { defineComponent, h, nextTick, ref } from 'vue';

/**
 * Checks if the DOM is available
 * @type {boolean}
 */
const canUseDOM = typeof window !== 'undefined';

/**
 * A teleport component that renders content to a specified target in the DOM
 */
export const CsTeleport = defineComponent({
  name: 'CsTeleport',
  props: {
    /**
     * Whether the teleport functionality is disabled
     * @type {boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * The order of the teleported content relative to other teleported content
     * @type {number}
     * @default 0
     */
    order: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { slots, emit }) {
    const MODAL_PORTAL_ID = 'cs-overlay-teleport-target';
    const portalTarget = ref(undefined);
    const targetId = ref(props.target);

    const createTeleportTarget = target => {
      let portalElement;
      const existingPortalElement = document.getElementById(target);

      if (existingPortalElement) {
        portalElement = existingPortalElement;
      } else {
        const el = document.createElement('div');
        el.id = target;
        if (document.body != null) {
          document.body.appendChild(el);
        }
        portalElement = el;
      }

      return portalElement;
    };

    const mountTarget = () => {
      if (!canUseDOM) {
        return;
      }

      if (!MODAL_PORTAL_ID) {
        console.warn('Portal target is undefined');
        return;
      }

      portalTarget.value = createTeleportTarget(MODAL_PORTAL_ID);

      if (portalTarget.value) {
        targetId.value = portalTarget.value.id;

        if (portalTarget.value.isConnected) {
          nextTick(() => {
            emit('portal:targetConnected');
          });
        }
      }
    };

    if (!props.disabled) {
      mountTarget();
    }

    return () => {
      const children = slots.default?.() || [];
      return !props.disabled
        ? h(
            MountingPortal,
            {
              props: {
                append: true,
                mountTo: `#${targetId.value}`,
                disabled: props.disabled,
                order: props.order,
                slim: true,
                targetSlim: true,
              },
              attrs: {
                'data-component': 'CsTeleport',
              },
            },
            children
          )
        : children[0];
    };
  },
});
